import { formatTitle } from "utils/formatTitle";
import { DynamicKeyValue } from "./DynamicKeyValue";
import { Show } from "shared/components";
import { cx } from "utils/classnames";
import { ReactNode } from "react";

function sortNotPresentToEnd<T>(
  entries: [string, T][],
  order: string[]
): [string, T][] {
  const present: [string, T][] = [];
  const notPresent: [string, T][] = [];

  // Separate elements present and not present in order
  entries.forEach((entry) => {
    if (order.includes(entry[0])) present.push(entry);
    else notPresent.push(entry);
  });

  // Sort elements present in order
  present.sort((a, b) => {
    const indexA = order.indexOf(a[0]);
    const indexB = order.indexOf(b[0]);
    return indexA - indexB;
  });

  // Concatenate present and not present elements
  const sortedEntries: [string, T][] = present.concat(notPresent);

  return sortedEntries;
}

// eslint-disable-next-line react-refresh/only-export-components
export function sortAndFilterObjectValues<Vals>(
  v: [string, Vals][],
  {
    order,
    filter
  }: {
    order?: string[];
    filter?: string[];
  }
) {
  return sortNotPresentToEnd(v, order || [])
    .sort(([keyA], [keyB]) => {
      if (!order) return 0;
      const idxA = order.indexOf(keyA);
      const idxB = order.indexOf(keyB);

      // Items which are not present in the order array will be sorted to the end of the list

      if (idxA === -1 && idxB === -1) return 0;

      if (idxA === -1) return 1;
      if (idxB === -1) return -1;

      return idxA - idxB;
    })
    .filter(([key]) => !filter || !filter.includes(key));
}

export const ObjectItem = ({
  fullWidthRow,
  detailsKey,
  value,
  skipEmptyObjects,
  order,
  filter,
  rename,
  parentObject,
  valueTransform,
  fullDate,
  status,
  displayTitle = true,
  actionElement
}: {
  displayTitle?: boolean;
  detailsKey: string;
  value: unknown;
  skipEmptyObjects?: boolean;
  order?: string[];
  filter?: string[];
  rename?: Record<string, string | ((object: object) => string)>;
  parentObject?: null | object;
  valueTransform?: Record<string, (value: any, row: object) => any>;
  fullDate?: boolean;
  status?: ReactNode;
  actionElement?: ReactNode;
  fullWidthRow?: boolean;
}) => {
  const title = formatTitle(detailsKey);

  if (title.includes("@"))
    return (
      <div>
        {typeof value === "object" ? (
          Object.entries(value || {})
            .sort(([keyA], [keyB]) => {
              if (!order) return 0;

              const idxA = order.indexOf(keyA);
              const idxB = order.indexOf(keyB);

              if (idxA === -1 && idxB === -1) return 0;
              if (idxA === -1) return 1;
              if (idxB === -1) return -1;

              return idxA - idxB;
            })
            .filter(([key]) => !filter || !filter.includes(key))
            .map(([k, v], idx) => (
              <DynamicKeyValue
                fullWidthRow={fullWidthRow}
                key={detailsKey + "_" + k}
                detailsKey={k}
                order={order}
                rename={rename}
                filter={filter}
                value={v}
                parentObject={parentObject || value}
                valueTransform={valueTransform}
                fullDate={fullDate}
                parentIdx={
                  !isNaN(Number(title.split("@ ")[1]))
                    ? Number(title.split("@ ")[1]) - 1
                    : idx
                }
              />
            ))
        ) : (
          <DynamicKeyValue
            fullWidthRow={fullWidthRow}
            skipEmptyObjects={skipEmptyObjects}
            detailsKey={detailsKey}
            order={order}
            filter={filter}
            rename={rename}
            valueTransform={valueTransform}
            value={value}
            fullDate={fullDate}
          />
        )}
      </div>
    );

  return (
    <div className={cx(displayTitle ? "mt-4" : "")}>
      <Show if={displayTitle}>
        <div className="border-b border-alpha-80 flex items-center justify-between gap-4 pb-2">
          <h3 className="min-w-0 text-sm font-bold">{title}</h3>
          <div className="flex shrink-0 items-center gap-4">
            {actionElement}
            {status}
          </div>
        </div>
      </Show>
      <div
        className={cx(
          "space-y-3",
          title.includes("Animal")
            ? ""
            : title === "Documents"
              ? "flex flex-wrap gap-4 py-4"
              : "py-4"
        )}
      >
        {typeof value === "object" ? (
          Object.entries(value || {})
            .sort(([keyA], [keyB]) => {
              if (!order) return 0;

              const idxA = order.indexOf(keyA);
              const idxB = order.indexOf(keyB);

              if (idxA === -1 && idxB === -1) return 0;
              if (idxA === -1) return 1;
              if (idxB === -1) return -1;

              return idxA - idxB;
            })
            .filter(([key]) => !filter || !filter.includes(key))
            .map(([k, v]) => (
              <DynamicKeyValue
                fullWidthRow={fullWidthRow}
                key={detailsKey + "_" + k}
                detailsKey={k}
                order={order}
                rename={rename}
                filter={filter}
                value={v}
                parentObject={value}
                valueTransform={valueTransform}
                fullDate={fullDate}
              />
            ))
        ) : (
          <DynamicKeyValue
            fullWidthRow={fullWidthRow}
            skipEmptyObjects={skipEmptyObjects}
            detailsKey={detailsKey}
            order={order}
            filter={filter}
            rename={rename}
            valueTransform={valueTransform}
            value={value}
            fullDate={fullDate}
          />
        )}
      </div>
    </div>
  );
};
